import { getAdminUrl, getMergedHeaders, useReapitConnectSession } from '@reapitform/hooks'
import { CopyTemplatesResult } from '../types'

/**
 * Imperative copy-templates helper. The source version is chosen at click time (in a drawer), so this can't
 * use the fixed-path `useAdminUpdate` hook. Clones the source version's event + action templates onto the
 * target (`POST /api/forms/{targetFormRef}/versions/{targetVersionRef}/templates/copy-from/{sourceVersionRef}`).
 * A 409 (target not empty, or self-copy) surfaces the server's message.
 */
export const useCopyFormTemplates = () => {
  const session = useReapitConnectSession()

  return async (
    targetFormReferenceID: string,
    targetVersionReferenceID: string,
    sourceVersionReferenceID: string,
  ): Promise<CopyTemplatesResult> => {
    const headers = await getMergedHeaders(session)
    if (!headers) throw new Error('Your session has expired — please sign in again.')

    const path = `/api/forms/${targetFormReferenceID}/versions/${targetVersionReferenceID}/templates/copy-from/${sourceVersionReferenceID}`
    const response = await fetch(getAdminUrl(path)(), {
      method: 'POST',
      headers: headers as HeadersInit,
    })

    if (!response.ok) {
      let message = `Copy failed (${response.status})`
      try {
        const body = await response.json()
        message = body?.description || body?.Description || message
      } catch {
        // Non-JSON body — keep the status-based message.
      }
      throw new Error(message)
    }

    return (await response.json()) as CopyTemplatesResult
  }
}
